import React, { useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import {
  Camera, Upload, Loader2, ScanLine, AlertTriangle, CheckCircle2, ArrowRight,
} from "lucide-react";

/**
 * Zulassungsbescheinigung Teil I scan → Review → Save flow.
 *
 * Props:
 *  - open, onOpenChange       (controlled)
 *  - customers: Customer[]    (list for picker)
 *  - presetCustomerId?: string
 *  - onSaved(vehicle)         called after successful save
 *  - allowCustomerCreate?: bool  default true
 */
const VEHICLE_FIELDS = [
  ["license_plate",  "vehicles.license_plate"],
  ["vin",            "vehicles.vin"],
  ["manufacturer",   "vehicles.brand"],
  ["model",          "vehicles.model"],
  ["hsn",            "scan.hsn"],
  ["tsn",            "scan.tsn"],
  ["first_registration",   "vehicles.first_reg"],
  ["current_registration", "scan.current_registration"],
  ["fuel_type",              "vehicles.fuel"],
  ["engine_displacement_cc", "scan.engine_cc"],
  ["power_kw",               "scan.power_kw"],
  ["vehicle_class",          "scan.vehicle_class"],
  ["color",                  "scan.color"],
  ["max_weight_kg",          "scan.max_weight"],
];
const OWNER_FIELDS = [
  ["owner_first_name", "customers.first_name"],
  ["owner_last_name",  "customers.last_name"],
  ["owner_address",    "customers.address"],
];

const LOW_CONFIDENCE_THRESHOLD = 0.6;

export default function ScanRegistrationDialog({
  open,
  onOpenChange,
  customers = [],
  presetCustomerId = null,
  onSaved,
  allowCustomerCreate = true,
}) {
  const { t } = useTranslation();
  const fileInputRef = useRef(null);
  const cameraInputRef = useRef(null);
  const [phase, setPhase] = useState("pick"); // pick | uploading | review
  const [extracted, setExtracted] = useState({});
  const [confidence, setConfidence] = useState({});
  const [existingCustomer, setExistingCustomer] = useState(null);
  const [existingVehicle, setExistingVehicle] = useState(null);
  const [customerMode, setCustomerMode] = useState(
    presetCustomerId ? "preset" : "create"
  );
  const [selectedCustomerId, setSelectedCustomerId] = useState(
    presetCustomerId || ""
  );
  const [saving, setSaving] = useState(false);

  const reset = () => {
    setPhase("pick");
    setExtracted({});
    setConfidence({});
    setExistingCustomer(null);
    setExistingVehicle(null);
    setSelectedCustomerId(presetCustomerId || "");
    setCustomerMode(presetCustomerId ? "preset" : "create");
    setSaving(false);
  };

  const handleFile = async (file) => {
    if (!file) return;
    setPhase("uploading");
    try {
      const fd = new FormData();
      fd.append("file", file);
      const r = await api.post("/vehicles/scan-registration", fd, {
        headers: { "Content-Type": "multipart/form-data" },
        timeout: 90000,
      });
      setExtracted(r.data.extracted || {});
      setConfidence(r.data.confidence || {});
      setExistingCustomer(r.data.existing_customer || null);
      setExistingVehicle(r.data.existing_vehicle || null);
      if (r.data.existing_customer && !presetCustomerId) {
        setCustomerMode("existing");
        setSelectedCustomerId(r.data.existing_customer.id);
      }
      setPhase("review");
    } catch (e) {
      toast.error(e?.response?.data?.detail || t("scan.error"));
      setPhase("pick");
    }
  };

  const setField = (k, v) => setExtracted((prev) => ({ ...prev, [k]: v }));

  const detectedOwnerFullName = useMemo(
    () => `${extracted.owner_first_name || ""} ${extracted.owner_last_name || ""}`.trim(),
    [extracted.owner_first_name, extracted.owner_last_name]
  );

  const selectedCustomer = useMemo(
    () => customers.find((c) => c.id === selectedCustomerId) || null,
    [customers, selectedCustomerId]
  );

  const ownerNameMismatch =
    (customerMode === "existing" || customerMode === "preset") &&
    !!selectedCustomer &&
    !!detectedOwnerFullName &&
    !selectedCustomer.name.toLowerCase().includes((extracted.owner_last_name || "").toLowerCase()) &&
    !detectedOwnerFullName.toLowerCase().includes(selectedCustomer.name.toLowerCase().split(" ").pop() || "");

  const save = async () => {
    if (!extracted.vin) {
      toast.error(t("scan.vin_required"));
      return;
    }
    if (existingVehicle) {
      toast.error(t("scan.vehicle_exists"));
      return;
    }
    setSaving(true);
    try {
      // 1) resolve customer_id
      let customerId = selectedCustomerId;
      if (customerMode === "create") {
        const name = detectedOwnerFullName || t("scan.unknown_owner");
        const r = await api.post("/customers", {
          name,
          address: extracted.owner_address || "",
          phone: "", email: "",
        });
        customerId = r.data.id;
      }
      if (!customerId) {
        throw new Error("no customer");
      }
      // 2) create the vehicle
      const veh = {
        customer_id: customerId,
        brand: extracted.manufacturer || "",
        model: extracted.model || "",
        license_plate: extracted.license_plate || "",
        vin: extracted.vin || "",
        first_registration: extracted.first_registration || "",
        fuel_type: extracted.fuel_type || "",
        engine: [
          extracted.engine_displacement_cc
            ? `${extracted.engine_displacement_cc} ccm`
            : "",
          extracted.power_kw ? `${extracted.power_kw} kW` : "",
        ].filter(Boolean).join(" · "),
        mileage: 0,
        color: extracted.color || "",
        hsn: extracted.hsn || "",
        tsn: extracted.tsn || "",
        vehicle_class: extracted.vehicle_class || "",
        max_weight_kg: extracted.max_weight_kg || null,
      };
      const rv = await api.post("/vehicles", veh);
      toast.success(t("scan.saved"));
      onSaved && onSaved(rv.data);
      onOpenChange(false);
      reset();
    } catch (e) {
      toast.error(e?.response?.data?.detail || t("scan.save_error"));
    } finally {
      setSaving(false);
    }
  };

  const isLow = (k) => (confidence[k] ?? 1) < LOW_CONFIDENCE_THRESHOLD;

  return (
    <Dialog
      open={open}
      onOpenChange={(v) => { onOpenChange(v); if (!v) reset(); }}
    >
      <DialogContent
        className="bg-[#21252E] border-[#2E3241] text-white max-w-2xl max-h-[92vh] overflow-y-auto"
        data-testid="scan-dialog"
      >
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <ScanLine className="h-5 w-5 text-blue-400" />
            {t("scan.title")}
          </DialogTitle>
        </DialogHeader>

        {phase === "pick" && (
          <div className="space-y-3" data-testid="scan-pick">
            <p className="text-sm text-zinc-400">{t("scan.pick_hint")}</p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <button
                type="button"
                className="surface rounded-xl p-6 hover:bg-[#1F232B] transition flex flex-col items-center gap-3 border border-[#2E3241]"
                onClick={() => cameraInputRef.current?.click()}
                data-testid="scan-camera-btn"
              >
                <Camera className="h-8 w-8 text-blue-400" />
                <div className="font-medium">{t("scan.take_photo")}</div>
                <div className="text-xs text-zinc-500">{t("scan.take_photo_hint")}</div>
              </button>
              <button
                type="button"
                className="surface rounded-xl p-6 hover:bg-[#1F232B] transition flex flex-col items-center gap-3 border border-[#2E3241]"
                onClick={() => fileInputRef.current?.click()}
                data-testid="scan-upload-btn"
              >
                <Upload className="h-8 w-8 text-blue-400" />
                <div className="font-medium">{t("scan.upload_file")}</div>
                <div className="text-xs text-zinc-500">{t("scan.upload_file_hint")}</div>
              </button>
            </div>
            <input
              ref={cameraInputRef}
              type="file"
              accept="image/*"
              capture="environment"
              className="hidden"
              onChange={(e) => handleFile(e.target.files?.[0])}
              data-testid="scan-camera-input"
            />
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*,application/pdf,.pdf"
              className="hidden"
              onChange={(e) => handleFile(e.target.files?.[0])}
              data-testid="scan-file-input"
            />
          </div>
        )}

        {phase === "uploading" && (
          <div className="p-10 flex flex-col items-center gap-3" data-testid="scan-uploading">
            <Loader2 className="h-8 w-8 animate-spin text-blue-400" />
            <div className="text-sm text-zinc-400">{t("scan.processing")}</div>
            <div className="text-xs text-zinc-500">{t("scan.processing_hint")}</div>
          </div>
        )}

        {phase === "review" && (
          <div className="space-y-4" data-testid="scan-review">
            {/* Duplicate VIN warning */}
            {existingVehicle && (
              <div className="rounded-lg border border-red-500/40 bg-red-500/10 p-3 text-sm space-y-1">
                <div className="flex items-center gap-2 font-medium text-red-300">
                  <AlertTriangle className="h-4 w-4" /> {t("scan.vehicle_exists")}
                </div>
                <div className="text-xs text-red-200">
                  {existingVehicle.brand} {existingVehicle.model} · {existingVehicle.license_plate} · VIN {existingVehicle.vin}
                </div>
              </div>
            )}

            {/* Owner-name mismatch warning */}
            {ownerNameMismatch && !existingVehicle && (
              <div className="rounded-lg border border-amber-500/40 bg-amber-500/10 p-3 text-xs text-amber-200 flex items-start gap-2">
                <AlertTriangle className="h-4 w-4 mt-0.5" />
                <div>
                  {t("scan.owner_mismatch", { detected: detectedOwnerFullName, selected: selectedCustomer?.name })}
                </div>
              </div>
            )}

            {/* Customer resolution block */}
            <div className="surface rounded-xl p-4 space-y-3">
              <div className="text-[11px] uppercase tracking-widest text-zinc-500">{t("scan.customer_section")}</div>
              {existingCustomer && !presetCustomerId && (
                <div className="rounded-md border border-emerald-500/40 bg-emerald-500/10 p-2 text-xs text-emerald-200 flex items-start gap-2">
                  <CheckCircle2 className="h-4 w-4 mt-0.5" />
                  <div>
                    {t("scan.existing_customer_found", { name: existingCustomer.name })}
                  </div>
                </div>
              )}
              {presetCustomerId ? (
                <div className="text-xs text-zinc-400">
                  {t("scan.preset_customer")}: <span className="text-zinc-100">{selectedCustomer?.name}</span>
                </div>
              ) : (
                <div className="flex gap-2 flex-wrap">
                  {allowCustomerCreate && (
                    <button
                      type="button"
                      onClick={() => setCustomerMode("create")}
                      data-testid="scan-customer-mode-create"
                      className={`text-xs px-3 py-1.5 rounded-md border ${customerMode === "create" ? "bg-blue-500/20 border-blue-400 text-blue-200" : "bg-[#171B24] border-[#2E3241] text-zinc-400"}`}
                    >
                      {t("scan.create_customer")}
                    </button>
                  )}
                  <button
                    type="button"
                    onClick={() => setCustomerMode("existing")}
                    data-testid="scan-customer-mode-existing"
                    className={`text-xs px-3 py-1.5 rounded-md border ${customerMode === "existing" ? "bg-blue-500/20 border-blue-400 text-blue-200" : "bg-[#171B24] border-[#2E3241] text-zinc-400"}`}
                  >
                    {t("scan.use_existing_customer")}
                  </button>
                </div>
              )}
              {customerMode === "existing" && !presetCustomerId && (
                <Select value={selectedCustomerId} onValueChange={setSelectedCustomerId}>
                  <SelectTrigger className="bg-[#171B24] border-[#2E3241]" data-testid="scan-customer-select">
                    <SelectValue placeholder={t("scan.pick_customer")} />
                  </SelectTrigger>
                  <SelectContent className="bg-[#21252E] border-[#2E3241] text-white max-h-64">
                    {customers.map((c) => (
                      <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
              {customerMode === "create" && (
                <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
                  {OWNER_FIELDS.map(([k, label]) => (
                    <ScanField key={k} k={k} label={t(label)}
                      value={extracted[k] || ""} onChange={setField}
                      low={isLow(k)} className={k === "owner_address" ? "md:col-span-3" : ""} />
                  ))}
                </div>
              )}
            </div>

            {/* Vehicle fields */}
            <div className="surface rounded-xl p-4 space-y-3">
              <div className="text-[11px] uppercase tracking-widest text-zinc-500">{t("scan.vehicle_section")}</div>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                {VEHICLE_FIELDS.map(([k, label]) => (
                  <ScanField key={k} k={k} label={t(label)}
                    value={extracted[k] ?? ""} onChange={setField}
                    low={isLow(k)}
                    className={k === "vin" ? "col-span-2 md:col-span-3" : ""} />
                ))}
              </div>
              <p className="text-[11px] text-zinc-500">
                {t("scan.low_confidence_legend")}
              </p>
            </div>
          </div>
        )}

        <DialogFooter className="pt-2">
          {phase === "pick" ? (
            <Button variant="ghost" onClick={() => onOpenChange(false)}>{t("common.cancel")}</Button>
          ) : phase === "review" ? (
            <>
              <Button variant="ghost" onClick={reset} data-testid="scan-back">
                {t("scan.back")}
              </Button>
              <Button
                onClick={save}
                disabled={saving || !!existingVehicle || (customerMode === "existing" && !selectedCustomerId)}
                className="bg-blue-500 hover:bg-blue-600 gap-2"
                data-testid="scan-save"
              >
                {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <ArrowRight className="h-4 w-4" />}
                {t("scan.save")}
              </Button>
            </>
          ) : null}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function ScanField({ k, label, value, onChange, low, className = "" }) {
  return (
    <div className={className}>
      <div className="flex items-center gap-1.5 mb-1">
        <Label className="text-[11px] uppercase tracking-widest text-zinc-500">{label}</Label>
        {low && (
          <Badge className="bg-amber-500/15 text-amber-300 border-amber-500/30 text-[9px] py-0 px-1.5">
            <AlertTriangle className="h-2.5 w-2.5 mr-1" /> low
          </Badge>
        )}
      </div>
      <Input
        value={value ?? ""}
        onChange={(e) => onChange(k, e.target.value)}
        className={`bg-[#171B24] border-[#2E3241] ${low ? "border-amber-500/50" : ""}`}
        data-testid={`scan-field-${k}`}
      />
    </div>
  );
}
